import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import {
  SortOrder,
  WithdrawalListOrderBy,
  WithdrawalRequestStatus,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { useWithdrawalApprovalsQuery } from "WithdrawalApprovals/Api/Queries/useWithdrawalApprovalsQuery";
import {
  useWithdrawalApprovalsTableColumns,
  WITHDRAWAL_SORTABLE_COLUMNS,
} from "WithdrawalApprovals/Components/useWithdrawalApprovalsTableColumns";

const SORT_COLUMN_MAP: Record<string, WithdrawalListOrderBy> = {
  actorName: WithdrawalListOrderBy.ActorName,
  amount: WithdrawalListOrderBy.Amount,
  status: WithdrawalListOrderBy.Status,
  requestedAt: WithdrawalListOrderBy.RequestedAt,
};

type Props = {
  fillHeight?: boolean;
};

export const PendingWithdrawalsTable = ({
  fillHeight,
}: Props): React.ReactNode => {
  const { t } = useResource();

  const { pageIndex, pageSize, onPaginationChange, sorting, onSortingChange } =
    useTableSearchParams({
      pageSize: 5,
      validSortIds: [...WITHDRAWAL_SORTABLE_COLUMNS],
      defaultSort: { id: "requestedAt", desc: true },
    });

  const orderBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data, isLoading } = useWithdrawalApprovalsQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    status: WithdrawalRequestStatus.Pending,
    orderBy,
    sortOrder,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const columns = useWithdrawalApprovalsTableColumns();

  const table = useReactTable({
    data: items,
    columns,
    getRowId: (row) => row.approvalToken,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  if (!isLoading && items.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: (theme) => `1px solid ${theme.colors.border}`,
          borderRadius: 2,
          bgcolor: "background.default",
          ...(fillHeight && { height: "100%" }),
        }}
      >
        <Stack alignItems="center" spacing={Spacing.S}>
          <CheckCircleOutlinedIcon
            sx={{ fontSize: 48, color: "primary.main" }}
          />
          <Typography variant="body1" color="text.secondary">
            {t(Resources.Dashboard.NoPendingWithdrawals)}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <BlTanstackTable
      table={table}
      isLoading={isLoading}
      totalCount={totalCount}
    />
  );
};
